// based on config returns the mutations
// none of these mutations should be called by frontend, they are meant to be used by actions to update state
// all changable things are exposed via actions
import cloneDeep from 'lodash.clonedeep'
import getProp from 'lodash.get'
import isEqual from 'lodash.isequal'
import pullAt from 'lodash.pullat'
import stateDefinition from './state'

const MAX_LOG_MESSAGES = 100

export default ({ config, common }) => {
  return {
    // logging
    addError (state, { message, error }) {
      state.messages.unshift({
        type: 'error',
        message: message,
        error: error
      })
      // keep only last N messages
      while (state.messages.length > MAX_LOG_MESSAGES) {
        state.messages.pop()
      }
      state.lastError = error
    },
    addInfo (state, { message }) {
      state.messages.unshift({
        type: 'info',
        message: message,
        error: null
      })
      // keep only last N messages
      while (state.messages.length > MAX_LOG_MESSAGES) {
        state.messages.pop()
      }
    },
    addWarning (state, { message }) {
      state.messages.unshift({
        type: 'warning',
        message: message,
        error: null
      })
      // keep only last N messages
      while (state.messages.length > MAX_LOG_MESSAGES) {
        state.messages.pop()
      }
    },
    // actual state
    appendItems (state, items) {
      state.items.push(...items)
      state.pagination.offset = state.pagination.offset + items.length
      if (items.length === 0) {
        state.allItemsLoaded = true
      }
    },
    clearActiveItem (state) {
      state.activeItem = null
    },
    clearCurrentlyEdited (state) {
      let ced = state.currentlyEdited
      if (ced.existing) {
        let ix = state.editingItems.findIndex(editedItem => getProp(editedItem.item, config.items.keyField, null) === getProp(ced.item, config.items.keyField, null))
        state.editingItems.splice(ix, 1)
      } else {
        state.newItems.splice(ced.newItemIndex)
      }
      state.currentlyEdited = null
    },
    clearErrors (state) {
      state.errors = []
    },
    clearItems (state) {
      state.items = []
      state.pagination.offset = 0
      state.allItemsLoaded = false
    },
    clearItemsByIndex (state, indexList) {
      pullAt(state.items, indexList)
      // notify Vue that array has changed
      state.items = [...state.items]
    },
    clearLatestRequests (state) {
      Object.keys(state.latestRequests).forEach(k => {
        state.latestRequests[k] = null
      })
    },
    clearMarkedByIndex (state, indexList) {
      pullAt(state.markedItemKeys, indexList)
      // notify Vue that array has changed
      state.markedItemKeys = [...state.markedItemKeys]
    },
    // checkbox / multiple selection logic
    // TODO: make alias?
    clearMarkedItems (state) {
      state.markedItemKeys = []
    },
    editItem (state, item) {
      // check if we are already editing the item, if not clone it
      let ix = state.editingItems.findIndex(editedItem => getProp(editedItem.item, config.items.keyField, null) === getProp(item, config.items.keyField, null))
      if (ix === -1) {
        let clonedItem = cloneDeep(item)
        state.editingItems.push({
          item: clonedItem,
          existing: true,
          newItemIndex: null
        })
        state.currentlyEdited = state.editingItems[state.editingItems.length - 1]
      } else {
        state.currentlyEdited = state.editingItems[ix]
      }
    },
    editNewItem (state, newItemIndex) {
      // check if we are already editing the item, if not clone it
      let ix = state.editingItems.findIndex(editedItem => editedItem.newItemIndex === newItemIndex)
      if (ix === -1) {
        state.editingItems.push({
          item: state.newItems[newItemIndex],
          existing: false,
          newItemIndex: newItemIndex
        })
        state.currentlyEdited = state.editingItems[state.editingItems.length - 1]
      } else {
        state.currentlyEdited = state.editingItems[ix]
      }
    },
    finishedLatestRequest (state, name) {
      // WRONG: we rely on having previous request for append/prepend logic
      // TODO: relying on previous request is not good because we might alter the list from master
      // --> make this applicable in production mode
      // state.latestRequests[name] = null
    },
    insertItems (state, { items, startAt }) {
      state.items.splice(startAt, 0, ...items)
      state.pagination.offset = state.pagination.offset + items.length
      if (items.length === 0) {
        state.allItemsLoaded = true
      }
    },
    markAllItems (state) {
      state.markedItemKeys.splice(0, state.markedItemKeys.length)
      state.markedItemKeys.push(...state.items.map(item => getProp(item, config.items.keyField, null)))
    },
    markItem (state, { item, marked = false }) {
      let key = getProp(item, config.items.keyField, null)
      let ix = state.markedItemKeys.findIndex(k => isEqual(key, k))
      if (marked && ix === -1) {
        state.markedItemKeys.push(key)
      }
      if (!marked && ix >= 0) {
        state.markedItemKeys.splice(ix, 1)
      }
    },
    markItemByKey (state, { key, marked = false }) {
      let ix = state.markedItemKeys.findIndex(k => isEqual(key, k))
      if (marked && ix === -1) {
        state.markedItemKeys.push(key)
      }
      if (!marked && ix >= 0) {
        state.markedItemKeys.splice(ix, 1)
      }
    },
    newItem (state) {
      let newItem
      if (typeof state.newItemTemplate === 'function') {
        newItem = state.newItemTemplate()
      }
      if (typeof state.newItemTemplate === 'object') {
        newItem = cloneDeep(state.newItemTemplate)
      }
      state.newItems.push(newItem)
      state.editingItems.push({
        item: newItem,
        existing: false,
        newItemIndex: state.newItems - 1
      })
      state.currentlyEdited = state.editingItems[state.editingItems.length - 1]
    },
    // patch the item within the list
    // doesn't touch the edited item
    patchItem (state, { key = undefined, partialData }) {
      let keyToCompare = key || getProp(partialData, config.items.keyField, undefined)
      let itemToPatch = state.items.find(item => isEqual(getProp(item, config.items.keyField, null), keyToCompare))
      if (itemToPatch) {
        // we assign the cloned data to prevent any changes outside of store
        let klone = cloneDeep(partialData)
        Object.assign(itemToPatch, klone)
      }
    },
    prependItems (state, items) {
      state.items.unshift(...items)
      state.pagination.offset = state.pagination.offset + items.length
      if (items.length === 0) {
        state.allItemsLoaded = true
      }
    },
    // reset is meant to be called upon logout
    // we create a completely fresh state
    reset (state) {
      let newState = stateDefinition({ config, common })()
      Object.assign(state, newState)
    },
    resetPagination (state) {
      state.pagination.offset = 0
      state.pagination.afterKeyList = []
    },
    setActiveItem (state, item) {
      state.activeItem = item
    },
    setAllItemsLoaded (state, loaded) {
      state.allItemsLoaded = loaded
    },
    setCurrentlyEditedItem (state, item) {
      state.currentlyEdited.item = item
    },
    setFilter (state, filter) {
      Object.assign(state.filter, filter)
    },
    setItems (state, items) {
      state.items = items
      state.allItemsLoaded = false
      state.pagination.offset = items.length
      // TODO: reset pagination and offset
      if (items.length === 0) {
        state.allItemsLoaded = true
      }
    },
    setLastPromise (state, { name, promise }) {
      state.lastPromises[name] = promise
    },
    // when saving the latest request, we track also who was the master
    // upon request end, we need to check if it's still valid
    setLatestRequest (state, { name, masterActiveKey, request }) {
      state.latestRequests[name] = {
        error: null,
        masterActiveKey: masterActiveKey,
        request: request,
        response: null
      }
      state.lastError = null
    },
    setLatestRequestError (state, { name, error }) {
      if (state.latestRequests[name]) {
        state.latestRequests[name].error = error
      }
    },
    setLatestResponse (state, { name, responseData }) {
      if (state.latestRequests[name]) {
        state.latestRequests[name].response = responseData
      }
    },
    setLoadingIndicator (state, { indicator, loading }) {
      state.loadingIndicators[indicator] = loading
    },
    setMasterActiveKey (state, masterActiveKey) {
      state.masterActiveKey = masterActiveKey
    },
    setPagination (state, pagination) {
      state.pagination = pagination
    },
    setPaginationAfterKeys (state, afterKeyList) {
      state.pagination.afterKeyList = afterKeyList
    },
    setSort (state, sort) {
      state.sort = sort
    }
  }
}
